import type {
  AudienceFilters,
  DemographicFilterQuery,
  IInstrumentDashboardSummary,
  QuestionDemographicDimension,
} from '@brushfire/types'
import { AGE_BUCKETS } from '@brushfire/utils/question-drilldown'
import { resolveActiveAudienceFiltersFromPublishConfig } from '@brushfire/utils/publish-audience-filters'

import { normalizeDeviceTypeValue } from '@/features/instruments/publish/target-audience/lib/deviceTypeFilter'

import {
  optionsForDemographicDimension,
  type DemographicFilterOption,
} from './instrument-demographic-filters'

function ageBucketRange(bucket: string): { min: number; max: number } {
  if (bucket === '65_plus') return { min: 65, max: 120 }
  const [minRaw, maxRaw] = bucket.split('-')
  return { min: Number(minRaw), max: Number(maxRaw) }
}

function ageBucketsOverlappingRange(min = 18, max = 120): string[] {
  return AGE_BUCKETS.filter((bucket) => {
    const { min: bucketMin, max: bucketMax } = ageBucketRange(bucket)
    return bucketMin <= max && bucketMax >= min
  })
}

function allowedAgeValues(audienceFilters: AudienceFilters): string[] | undefined {
  const ageRange = audienceFilters.ageRange
  if (!ageRange || (ageRange.min == null && ageRange.max == null)) return undefined
  return ageBucketsOverlappingRange(ageRange.min, ageRange.max)
}

function allowedLocationValue(
  location: AudienceFilters['location'],
  field: 'state' | 'city',
): string[] | undefined {
  const value = location?.[field]?.trim()
  return value ? [value] : undefined
}

function allowedCheckboxValues(values: readonly string[] | undefined): string[] | undefined {
  return values?.length ? [...values] : undefined
}

function allowedDeviceTypeValues(audienceFilters: AudienceFilters): string[] | undefined {
  const channels = normalizeDeviceTypeValue(audienceFilters.deviceType)
  return channels?.length ? channels : undefined
}

/** Mirrors campaign-service `allowedValuesForDimension` for client-side Add Filter guard (BFP-680 AC-3). */
export function buildPublishTimeFilterCatalog(
  audienceFilters: AudienceFilters,
): DemographicFilterQuery {
  const catalog: DemographicFilterQuery = {}

  const age = allowedAgeValues(audienceFilters)
  if (age?.length) catalog.age = age

  const gender = allowedCheckboxValues(audienceFilters.gender)
  if (gender?.length) catalog.gender = gender

  const region = allowedLocationValue(audienceFilters.location, 'state')
  if (region?.length) catalog.region = region

  const city = allowedLocationValue(audienceFilters.location, 'city')
  if (city?.length) catalog.city = city

  const education = allowedCheckboxValues(audienceFilters.education)
  if (education?.length) catalog.education = education

  const employment = allowedCheckboxValues(audienceFilters.employment)
  if (employment?.length) catalog.employment = employment

  const deviceType = allowedDeviceTypeValues(audienceFilters)
  if (deviceType?.length) catalog.deviceType = deviceType

  const maritalStatus = allowedCheckboxValues(audienceFilters.maritalStatus)
  if (maritalStatus?.length) catalog.maritalStatus = maritalStatus

  const householdSize = allowedCheckboxValues(audienceFilters.householdSize)
  if (householdSize?.length) catalog.householdSize = householdSize

  const numberOfChildren = allowedCheckboxValues(audienceFilters.numberOfChildren)
  if (numberOfChildren?.length) catalog.numberOfChildren = numberOfChildren

  const industry = allowedCheckboxValues(audienceFilters.industry)
  if (industry?.length) catalog.industry = industry

  const housingStatus = allowedCheckboxValues(audienceFilters.housingStatus)
  if (housingStatus?.length) catalog.housingStatus = housingStatus

  return catalog
}

function isDemographicFilterQuery(value: unknown): value is DemographicFilterQuery {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Reads publish-time allow-list from dashboard summary (brownfield / forward-compatible). */
export function extractDemographicFilterCatalog(
  raw: IInstrumentDashboardSummary & Record<string, unknown>,
): DemographicFilterQuery | undefined {
  if (isDemographicFilterQuery(raw.demographicFilterCatalog)) {
    return raw.demographicFilterCatalog
  }

  const publishConfig = raw.publishConfig
  if (publishConfig != null && typeof publishConfig === 'object') {
    const audienceFilters = resolveActiveAudienceFiltersFromPublishConfig(publishConfig)
    const catalog = buildPublishTimeFilterCatalog(audienceFilters)
    return Object.keys(catalog).length > 0 ? catalog : undefined
  }

  const targetAudience = raw.targetAudience
  if (targetAudience != null && typeof targetAudience === 'object') {
    const audienceFilters = resolveActiveAudienceFiltersFromPublishConfig({ targetAudience })
    const catalog = buildPublishTimeFilterCatalog(audienceFilters)
    return Object.keys(catalog).length > 0 ? catalog : undefined
  }

  return undefined
}

export function publishTimeOptionsForDemographicDimension(
  dimension: QuestionDemographicDimension,
  catalog?: DemographicFilterQuery,
): DemographicFilterOption[] {
  const allOptions = optionsForDemographicDimension(dimension)
  const allowedValues = catalog?.[dimension]
  if (!allowedValues?.length) return allOptions

  const allowed = new Set(allowedValues)
  return allOptions.filter((option) => allowed.has(option.value))
}
